import Papa from 'papaparse';
import {ObjectHelper, Container} from "@bryntum/schedulerpro";
import {getCSVMappings} from "../../../network/network.jsx";
import {removeEscapeSequences, showConfirmationDialog, showToast} from "../../../other/utils/utils.jsx";
import {userPreferences} from "../../../react/context/bryntumContext.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../../other/constants/PersistenceStateKeys.jsx";
import { EDITOR_KEYS } from '../../../other/constants/EditorKeys.jsx';
import { WIDGET_KEYS } from '../../../other/constants/WidgetKeys.jsx';

export default class MapTab extends Container {
  static get $name() {
    return "MapTab";
  }

  static get type() {
    return "maptab";
  }

  static get configurable() {
    return {
      title: "Map",
      items: {
        budgetFieldsGrid: {
          type: WIDGET_KEYS.swivuPreferenceStyleGrid,
          height: "100%",
          features: {
            cellMenu: false
          },
          columns: [
            {
              id: "task-budget-fields",
              field: "budgetField",
              text: "Task Budget Fields",
              flex: 1,
              editor: false,
              sortable: false,
              groupable: false,
            },
            {
              id: "imported-fields",
              type: WIDGET_KEYS.importedFieldsColumn,
            }
          ],
          bbar: {
            layoutStyle: {
              justifyContent: "space-between",
            },
            contentElementCls: "!pr-0",
            items: [
              { type: "button", text: "Save Mapping", onClick: "up.onSaveMapping" },
              { type: "button", text: "Finalize", cls: "b-raised", color: "b-blue", onClick: "up.onFinalizeCSV" }
            ]
          },
          listeners: {
            cellClick: "up.onGridCellClick"
          }
        }
      }
    }
  }

  onSaveMapping() {
    const { budgetFieldsGrid } = this.widgetMap;
    const mappedFields = budgetFieldsGrid.store.reduce((fieldMapping, fieldColumn) => {
      fieldMapping[fieldColumn.budgetField] = fieldColumn.importedField || "";
      return fieldMapping;
    }, {});
    userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.budgetMappedFieldsKey, JSON.stringify(mappedFields));
    showToast({ msg: "The budget mapping has been saved.", type: "success" });
  }

  getCsvHeaderInLastSavedOrder(importedCsvHeaders) {
    const userLastSavedBudgetFieldsMapping = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.budgetMappedFieldsKey);
    const csvHeaderMap = {};
    if (userLastSavedBudgetFieldsMapping) {
      const userLastSavedMappingPrefs = JSON.parse(userLastSavedBudgetFieldsMapping);
      /* We are only setting values for those 'budgetFields' when the value of these budgetFields from the preference are in the importedCsvHeaders, otherwise we are setting them to stay empty. */
      this.constantHeaders.forEach(header => {
        const headersValueInPreference = userLastSavedMappingPrefs[header];
        const isHeaderValueInImportedHeaders = importedCsvHeaders.some(item => item === headersValueInPreference);
        if (isHeaderValueInImportedHeaders) {
          csvHeaderMap[header] = userLastSavedMappingPrefs[header];
        } else {
          csvHeaderMap[header] = "";
        }
      })
    } else {
      /* If preference is not saved then just show importedCsvHeaders in their csv order */
      this.constantHeaders.forEach((header, index) => {
        csvHeaderMap[header] = importedCsvHeaders[index];
      })
    }
    return csvHeaderMap;
  }

  async editRecord(record, context, { files, onFinalizeImport }) {
    try {
      this.originalFile = files[0];
      this.context = context;
      this.mask("Loading...");
      this.onFinalizeImport = onFinalizeImport;
      const { budgetFieldsGrid } = this.widgetMap;
      const { data } = await getCSVMappings(context.selectedBoard.record.slug, record.importType);
      this.constantHeaders = data.headers;
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvString = e.target.result;
        let [csvHeader, ...csvDataRows] = Papa.parse(csvString, { skipEmptyLines: "greedy" }).data;
        //Todo: We need to remove the escape sequences from the last column
        // For Now we are temporary fixing this issue,
        // Need to explore more test cases and options for this
        csvDataRows = csvDataRows.map((row) => {
          row[row.length - 1] = row[row.length - 1].replace("\n", " ").trim();
          return row;
        })

        const nonEscapedHeaders = csvHeader.map(removeEscapeSequences);
        const csvHeaderModified = this.getCsvHeaderInLastSavedOrder(nonEscapedHeaders);
        const importedFieldColumn = budgetFieldsGrid.columns.getById("imported-fields");
        importedFieldColumn.onLoad(nonEscapedHeaders)
        budgetFieldsGrid.store.data = this.constantHeaders.map((field, recordIndex) => ({
          id: recordIndex,
          budgetField: field,
          importedField: csvHeaderModified[field]
        }));
        this.csvDataRows = csvDataRows;
        this.csvHeader = csvHeader;
      }
      reader.readAsText(this.originalFile)
    } catch (e) {
      console.log(e);
    } finally {
      this.unmask();
    }
  }

  onFinalizeCSV() {
    showConfirmationDialog({
      title: "Confirm Overwrite?",
      message: `Are you sure you want to overwrite your existing records in the budget grid?`,
    }).then((isOverwrite) => {
      if (isOverwrite) {
        const { budgetFieldsGrid } = this.widgetMap;
        this.mask('Loading...')
        const mappedBudgetHeaders = budgetFieldsGrid.store.map((budgetField) => budgetField.importedField);
        const clonedCsvDataRows = ObjectHelper.clone(this.csvDataRows);
        mappedBudgetHeaders.forEach((mbhItem, mbhIdx) => {
          const cellValueIndex = this.csvHeader.indexOf(mbhItem);
          clonedCsvDataRows.forEach((ccDrItem, ccdrIdx) => {
            ccDrItem[mbhIdx] = this.csvDataRows[ccdrIdx][cellValueIndex] || ""
          });
        });
        const unparsed = Papa.unparse([this.constantHeaders, ...clonedCsvDataRows])
        const file = new File([unparsed], this.originalFile.name, { type: "text/csv" });
        this.onFinalizeImport(file).then(() => {
          this.unmask();
        })
      }
    })
  }

  onGridCellClick({ column, record, event }) {
    if (column.id === "imported-fields") {
      this.context.editorHandler.openEditor(
        EDITOR_KEYS.entityTagEditor,
        null,
        {
          entityRecord: record,
          event,
          column
        },
        { by: "recordType", useExisting: true }
      );
    }
  }
}

MapTab.initClass();
